import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Card, Spin, Input, Pagination, Select } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { motion } from "framer-motion";
import { AuthContext } from "../../context/AuthContext";

const { Search } = Input;
const { Option } = Select;

export default function LeaveUtilization() {
  const { authData, backUrl } = useContext(AuthContext) || { backUrl: "" };
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [organisationId, setOrganisationId] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [organisations, setOrganisations] = useState([]);
  const pageSize = 10;

  // Fetch organisations
  const fetchOrganisation = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
        headers: {
          Authorization: `Bearer ${authData?.access}`,
        },
      });
      setOrganisations(res.data.results || []);
    } catch (err) {
      console.error("Error fetching organisations:", err);
    }
  };

  useEffect(() => {
    fetchOrganisation();
  }, []);

  // Fetch leave utilization data
  useEffect(() => {
    const fetchLeaveData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${backUrl}/api/reports/leave-utilization/?organisation_id=${organisationId}`
        );
        setData(res.data);
        setFilteredData(res.data);
      } catch (error) {
        console.error("Error fetching leave utilization data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaveData();
  }, [organisationId, backUrl]);

  const COLORS = ["#22C55E", "#3B82F6", "#F59E0B"];

  // Search employee
  const handleSearch = (value) => {
    const lower = value.toLowerCase();
    const results = data.filter((d) =>
      d.employee.toLowerCase().includes(lower)
    );
    setFilteredData(results);
    setCurrentPage(1);
  };

  // Pagination logic
  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen flex flex-col items-center">
      {/* Filters */}
      <Card
        className="w-full max-w-6xl shadow-sm rounded-2xl mb-8"
        bodyStyle={{ padding: "20px 24px" }}
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Select
              value={organisationId}
              onChange={(value) => setOrganisationId(value)}
              style={{ width: 280 }}
              placeholder="Select organisation"
            >
              {organisations.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
             <Search
            placeholder="Search employee..."
            allowClear
            onSearch={handleSearch}
            style={{ width: 200, marginLeft:'2px' }}
          />
          </div>
         
        </div>
      </Card>

      {/* Chart Section */}
      <Card className="w-full max-w-6xl shadow-md rounded-2xl">
        {loading ? (
          <div className="flex justify-center items-center h-96">
            <Spin size="large" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {paginatedData.length === 0 ? (
              <p className="text-center text-gray-500 py-16 text-lg">
                No records found.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                <BarChart
                  data={paginatedData}
                  margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="employee"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={80}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="total_eligible" fill={COLORS[0]} name="Total Eligible" />
                  <Bar dataKey="total_availed" fill={COLORS[1]} name="Total Availed" />
                  <Bar dataKey="balance" fill={COLORS[2]} name="Balance" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </motion.div>
        )}
      </Card>
      <br/>

      {/* Pagination */}
      {filteredData.length > pageSize && (
        <div className="mt-6">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={filteredData.length}
            onChange={(page) => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
}
